import { useParams, Link } from "react-router";
import { TutorialHeader } from "@/components/tutorial/TutorialHeader";
import { TutorialInfo } from "@/components/tutorial/TutorialInfo";
import { TutorialSteps } from "@/components/tutorial/TutorialSteps";
import { TutorialCompletion } from "@/components/tutorial/TutorialCompletion";
import { TutorialComments } from "@/components/tutorial/TutorialComments";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MuxPlayer from "@mux/mux-player-react";
import { useQuery } from "@tanstack/react-query";

import { AspectRatio } from "@/components/ui/aspect-ratio";
import { CardContainer } from "@/components/ui/card";

import {
  fetchTutorial,
  Tutorial as TutorialType,
  RichTextBlock,
  MediaBlock,
  QuoteBlock,
  SliderBlock,
  BaseBlock,
  API_URL,
} from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MainLayout } from "@/components/layouts/MainLayout";
import {
  IMTutorialView,
  MixpanelActions,
} from "@/integrations/mixpanel/mixpanel";
import { TutorialActions } from "../components/TutorialOverview/TutorialActions";
import { useUser } from "@supabase/auth-helpers-react";
import { useSavedTutorials } from "../hooks/useSavedTutorials";
import { useUninterestedTutorials } from "../hooks/useUninterestedTutorials";

interface ITutorial {
  id: string;
  documentId: string;
  title: string;
  description: string;
  slug: string | null;
  updatedAt: Date;
  publishedAt: Date;
  duration: string | null;
  thumbnailUrl?: string;
  tags?: string[];
  tools?: Array<{
    id: number;
    documentId: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
  }>;
  creator?: {
    name: string;
    avatar: string;
  };
}

interface VideoBlock extends BaseBlock {
  __component: "shared.video";
  video: {
    id: number;
    documentId: string;
    title: string;
    playback_id: string | null;
    isReady: boolean;
  };
}

interface SharedTutorialBlock extends BaseBlock {
  __component: "shared.tutorial";
  tutorial: ITutorial;
}

// Helper component to render different block types
const TutorialBlock = ({
  block,
  tutorial,
}: {
  block: TutorialType["blocks"][0] | VideoBlock | SharedTutorialBlock;
  tutorial: ITutorial;
}) => {
  const [watchTime, setWatchTime] = useState(0);
  const watchTimeRef = useRef(0);
  const lastTimeRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const sendStats = useCallback(() => {
    if (watchTimeRef.current > 0) {
      MixpanelActions.videoViewed({
        video_id: (block as VideoBlock).video.id,
        video_title: (block as VideoBlock).video.title,
        tutorial_id: tutorial?.documentId,

        watch_time_seconds: Math.round(watchTimeRef.current),
      });
    }
  }, [block, tutorial?.documentId]);

  useEffect(() => {
    const handleBeforeUnload = () => sendStats();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      sendStats();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [sendStats]);

  const handlePlay = () => {
    lastTimeRef.current = Date.now();
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const delta = (now - lastTimeRef.current) / 1000;
        watchTimeRef.current += delta;
        setWatchTime(watchTimeRef.current);
        lastTimeRef.current = now;
      }, 1000);
    }
  };
  const handleEnded = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (watchTime > 30) {
      sendStats();
    }
  };

  const handlePause = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;

    if (watchTime > 150) {
      sendStats();
    }
  };

  switch (block.__component) {
    case "shared.rich-text": {
      return (
        <CardContainer>
          <div className="prose prose-stone dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {(block as RichTextBlock).body}
            </ReactMarkdown>
          </div>
        </CardContainer>
      );
    }

    case "shared.quote": {
      return (
        <CardContainer>
          <blockquote className="border-l-4 border-primary pl-4">
            <p className="italic mb-2">{(block as QuoteBlock).body}</p>
            {(block as QuoteBlock).title && (
              <footer className="text-sm">
                — {(block as QuoteBlock).title}
              </footer>
            )}
          </blockquote>
        </CardContainer>
      );
    }

    case "shared.media": {
      const media = block as MediaBlock;
      return (
        <CardContainer>
          <figure>
            <img
              src={media.file.formats.medium.url}
              alt={media.file.alternativeText || ""}
              className="rounded-lg w-full"
            />
            {media.file.caption && (
              <figcaption className="text-center text-sm mt-2 text-muted-foreground">
                {media.file.caption}
              </figcaption>
            )}
          </figure>
        </CardContainer>
      );
    }

    case "shared.slider": {
      const slider = block as SliderBlock;
      return (
        <CardContainer>
          <div className="flex gap-4 overflow-x-auto">
            {slider.files.map((file, index) => (
              <img
                key={index}
                src={file.formats.medium.url}
                alt={file.alternativeText || ""}
                className="rounded-lg max-w-md"
              />
            ))}
          </div>
        </CardContainer>
      );
    }

    case "shared.video": {
      const videoBlock = block as VideoBlock;
      if (videoBlock.video.playback_id && videoBlock.video.isReady) {
        return (
          <CardContainer>
            <MuxPlayer
              playbackId={videoBlock.video.playback_id}
              metadata={{
                video_title: videoBlock.video.title,
              }}
              onPlay={handlePlay}
              onPause={handlePause}
              onEnded={handleEnded}
            />
          </CardContainer>
        );
      }
      return null;
    }

    case "shared.tutorial": {
      const tutorialBlock = block as SharedTutorialBlock;
      const nestedTutorial = tutorialBlock.tutorial;
      // Encode the documentId for the URL path
      const encodedDocumentId = encodeURIComponent(nestedTutorial.documentId);

      return (
        <CardContainer>
          <div className="space-y-2">
            {/* Title and link */}
            <div className="flex flex-row items-center justify-between gap-4">
              <Link
                to={`/tutorial/${encodedDocumentId}`}
                className="hover:underline flex-grow"
              >
                <h3 className="font-medium text-lg">{nestedTutorial.title}</h3>
              </Link>

              {/* Render the completion component */}
              <TutorialCompletion
                documentId={nestedTutorial.documentId}
                variant="buttonOnly"
              />
            </div>

            {/* Description */}
            {nestedTutorial.description && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                {nestedTutorial.description}
              </p>
            )}
          </div>
        </CardContainer>
      );
    }

    default:
      return null;
  }
};

const Tutorial = () => {
  const user = useUser();
  const { documentId } = useParams<{ documentId: string; }>();
  const [isCompleted, setIsCompleted] = useState(false);
  const { isTutorialSaved, toggleSavedTutorial } = useSavedTutorials(user?.id);
  const { isUninterested, toggleUninterested } = useUninterestedTutorials(
    user?.id
  );

  const {
    data: tutorial,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["tutorial", documentId],
    queryFn: () => fetchTutorial(documentId!),
  });

  const mxPanelTutorial: IMTutorialView = useMemo(
    () => ({
      tutorial_id: tutorial?.documentId,
      tutorial_title: tutorial?.title,
      author: tutorial?.creator.name,
      tags: tutorial?.tags,
      duration: tutorial?.duration,
      steps: tutorial?.blocks.length,
    }),
    [tutorial]
  );

  useEffect(() => {
    if (tutorial) {
      MixpanelActions.tutorialViewed(mxPanelTutorial);
    }
  }, [mxPanelTutorial, tutorial]);

  const handleSaveToggle = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!user) return;
      await toggleSavedTutorial(tutorial.documentId);
    },
    [user, tutorial, toggleSavedTutorial]
  );

  const handleNotInterested = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!user?.id) return;
      await toggleUninterested(tutorial.documentId);
    },
    [user?.id, tutorial, toggleUninterested]
  );

  if (isLoading) {
    return (
      <MainLayout>
        <div className="max-w-5xl mx-auto px-4 w-full">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded w-1/2" />
            <div className="aspect-video bg-muted rounded-lg" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !tutorial) {
    return (
      <MainLayout>
        <div className="max-w-5xl mx-auto px-4 w-full">
          <div className="text-center text-destructive">
            {error instanceof Error ? error.message : "Tutorial not found"}
          </div>
        </div>
      </MainLayout>
    );
  }
  const firstVideoIndex = tutorial?.blocks.findIndex(
    (e) => e.__component === "shared.video"
  );
  const videoThumbnail = tutorial?.blocks[firstVideoIndex];
  const tutorialBlocks = [
    ...(tutorial?.blocks
      ? tutorial.blocks.filter((_, idx) => idx !== firstVideoIndex)
      : []),
  ];

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto px-4 w-full">
        <CardContainer>
          <TutorialHeader
            title={tutorial.title}
            description={tutorial.description}
          />
          {/* <TutorialActions
            classNames={{
              wrapper: 'justify-end',
              buttonText: '!inline-block'
            }}
            id={tutorial.id}
            saved={isTutorialSaved(tutorial.documentId)}
            onSave={handleSaveToggle}
            onNotInterested={handleNotInterested}
            isUninterested={isUninterested(tutorial.documentId)}
          /> */}
        </CardContainer>
        <TutorialInfo
          author={tutorial.creator.name}
          date={tutorial.publishedAt.toLocaleDateString()}
          steps={tutorial.blocks.length}
          duration={tutorial.duration}
          complexity={tutorial.complexity}
          tools={tutorial.tools}
          tags={tutorial.tags}
        />
        {videoThumbnail ? (
          <TutorialBlock block={videoThumbnail} tutorial={tutorial} />
        ) : (
          tutorial.thumbnailUrl && (
            <CardContainer>
              <AspectRatio
                ratio={16 / 9}
                className="bg-muted rounded-lg overflow-hidden"
              >
                <img
                  src={tutorial.thumbnailUrl}
                  alt={tutorial.title}
                  className="w-full h-full object-cover"
                />
              </AspectRatio>
            </CardContainer>
          )
        )}
        {/*<TutorialRelated tutorials={[]} />*/}
        <TutorialSteps steps={[]} />
        {/* Render blocks */}
        <div className="mt-8">
          {tutorialBlocks &&
            tutorialBlocks.map((block) => (
              <TutorialBlock key={block.id} block={block} tutorial={tutorial} />
            ))}
        </div>
        <TutorialCompletion
          documentId={documentId}
          isCompleted={isCompleted}
          onComplete={() => {
            MixpanelActions.tutorialIsCompleted(mxPanelTutorial);
            setIsCompleted((prev) => !prev);
          }}
          tutorialButtons={
            <TutorialActions
              classNames={{
                wrapper: "justify-end",
                buttonText: "!inline-block",
              }}
              id={tutorial.id}
              saved={isTutorialSaved(tutorial.documentId)}
              onSave={handleSaveToggle}
              onNotInterested={handleNotInterested}
              isUninterested={isUninterested(tutorial.documentId)}
              isOnlyForSave
            />
          }
        />
        <TutorialComments
          comments={[]}
          tutorialId={documentId || ""}
          tutorialTitle={tutorial.title}
        />
      </div>
    </MainLayout>
  );
};

export default Tutorial;
